<template>
  <div class="all-items-block">
    <div class="all-items-block_filtration">
      <div class="all-items-block_filtration__group">
        <div
          v-if="isInModal"
          class="all-items-block_filtration__group-item"
        >
          <slot name="datepicker" />
        </div>
        <div class="all-items-block_filtration__group-item">
          <MSelectControl
            id="all-items-block-m-select-control_1"
            dropdown-boundary-element="all-items-block-m-select-control_1"
            :is-in-modal="isInModal"
            :value="activeGroupId"
            :choices="choicesGroups"
            :small="true"
            :disabled="disableGroupSelect || disabled"
            id-attr="id"
            text-attr="name"
            :label="groupNameText"
            class="all-items-block-filtration__group-select"
            @input="$emit('selectGroup', $event)"
          />
        </div>
      </div>
      <div class="all-items-block-filtration__search all-items-search">
        <label
          v-if="entityNameText"
          for="filtration-input"
          class="all-items-search__label"
        >
          {{ entityNameText }}
        </label>
        <MSearchInline
          v-model="searchText"
          input-id="filtration-input"
          :small="true"
          :icon="searchIcon"
          :placeholder="_getTr('search')"
          :emit-input-on-change="false"
          :is-debounced="false"
          class="all-items-search__input"
        />
      </div>
    </div>
    <div class="all-items-block__choices">
      <div
        v-if="filteredChoices.length > 0 && !disabled"
        class="all-items-block__select-all"
      >
        <MCheckbox
          :label-text="selectAllText"
          :value="areAllSelected"
          :intermediate="isIntermediate"
          class="all-items-block-choices__item"
          @input="handleSelectAll"
        />
        <MHelp
          v-if="selectAllHint"
          width="200px"
          class="all-items-block-select-all__help-icon"
        >
          {{ selectAllHint }}
        </MHelp>
      </div>

      <div
        v-for="(chunk, chunkId) in chunkedChoices"
        :key="chunkId"
        v-observe-visibility="getObserverConfig(chunkId)"
        class="m-lazy-render__chunk m-lazy-render__lazy-enabled"
        :style="getCssVarsForChunk(chunkId)"
      >
        <template v-if="!chunkIsVisible(chunkId)">
          <div class="m-lazy-render__lazy-dummy-node" />
        </template>
        <template v-else>
          <MCheckbox
            v-for="item in chunk"
            :id="`all-items-block-checkbox-${item.id}`"
            :key="item.id"
            :disabled="disabled"
            :label-text="item.name"
            :value="itemIsActive(item)"
            class="all-items-block-choices__item"
            @input="handleItemSelect(item)"
          />
        </template>
      </div>

      <MHelp
        v-for="item in boundAndDisabledChoices"
        :key="`all-items-block-m-help-${item.id}`"
        :custom-reference="true"
        :reference-selector="`#all-items-block-checkbox-${item.id}`"
        placement="bottom"
        :visible-arrow="false"
        :popper-options="{modifiers: {
          preventOverflow: {
            enabled: true
          }
        }}"
        width="260px"
        class="bound-and-disabled-checkbox-m-help"
      >
        <template slot="reference">
          <MCheckbox
            :id="`all-items-block-checkbox-${item.id}`"
            :label-text="item.name"
            :disabled="item.disabled === true || disabled"
            :value="itemIsActive(item)"
            :allow-select-by-label-click="false"
            class="all-items-block-choices__item"
            @input="handleItemSelect(item)"
          />
        </template>
        <template slot="content">
          <PlacementHint
            :data="item.orders"
            :is-intersection-dates="item.disabled"
          />
        </template>
      </MHelp>
    </div>
  </div>
</template>

<script>
  import MSelectControl from 'components/Inputs/Select/MSelectControl/MSelectControl';
  import searchIcon from 'assets/icons/search.svg';
  import MSearchInline from 'components/common/MSearchInline';
  import _ from 'lodash';
  import MCheckbox from 'components/Inputs/Basic/MCheckbox';
  import getTr from 'translation';
  import MHelp from 'components/Help/MHelp';
  import PlacementHint from 'components/Inputs/Select/PlacementsSelect/PlacementHint';
  import defaultSearchFunction from 'components/Inputs/Select/utils/defaultSearchFunction';
  import LazyRenderMixin from 'components/mixins/LazyRenderMixin';
  _.noConflict();


  const ITEM_ELEMENT_HEIGHT = 22;


  export default {
    name: 'AllItemsBlock',
    components: {MHelp, MCheckbox, MSearchInline, MSelectControl, PlacementHint},
    mixins: [LazyRenderMixin],
    props: {
      activeGroupId: {
        type: [Number, String],
        default: null
      },
      choices: {
        type: Array,
        required: true
      },
      value: {
        type: Array,
        required: true
      },
      disabled: {
        type: Boolean,
        default: false
      },
      groupNameText: {
        type: String,
        default: ''
      },
      entityNameText: {
        type: String,
        default: ''
      },
      disableGroupSelect: {
        type: Boolean,
        default: false
      },
      selectAllText: {
        type: String,
        default: () => getTr('select_all')
      },
      selectAllHint: {
        type: String,
        default: ''
      },
      selectAllPredicate: {
        type: Function,
        default: () => true
      },
      searchFunction: {
        type: Function,
        default: defaultSearchFunction
      },
      isInModal: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        searchIcon,
        searchText: ''
      };
    },
    computed: {
      singleElementHeight () {
        return ITEM_ELEMENT_HEIGHT;
      },
      chunkedChoices () {
        return _.chunk(this.freeChoices, this.lazyRenderChunkSize);
      },
      isIntermediate () {
        return !this.areAllSelected && this.value.length !== 0;
      },
      choicesGroups () {
        return this.choices.map(group => _.omit(group, ['children']));
      },
      activeGroupChoices () {
        const activeGroup = this.choices.find(g => g.id === this.activeGroupId);
        if (!activeGroup) {
          return [];
        }
        return activeGroup.children;
      },
      freeChoices () {
        return this.activeGroupChoices.filter(c => !(c.boundToOtherOrder || c.disabled));
      },
      boundAndDisabledChoices () {
        const bound = this.activeGroupChoices.filter(c => c.boundToOtherOrder && !c.disabled);
        const disabled = this.activeGroupChoices.filter(c => c.disabled);

        return bound.concat(disabled);
      },
      filteredChoices () {
        if (this.searchText) {
          return this.activeGroupChoices.filter(c => this.searchFunction(c, this.searchText, this));
        }
        return this.activeGroupChoices;
      },
      filteredSelectAllChoices () {
        return this.filteredChoices.filter(c => this.selectAllPredicate(c));
      },
      areAllSelected () {
        const availableIds = this.filteredSelectAllChoices.map(c => c.id);
        const intersection = _.intersection(availableIds, this.value);
        return intersection.length !== 0 && intersection.length === availableIds.length;
      }
    },
    methods: {
      highlightInfo (isComplexHint) {
        if (isComplexHint) {
          return {isComplexHint};
        }
        return null;
      },
      itemIsActive (item) {
        return this.value.includes(item.id);
      },
      handleItemSelect (item) {
        if (this.itemIsActive(item)) {
          return this.$emit('input', this.value.filter(c => c !== item.id));
        }
        return this.$emit(
          'input',
          [
            ...this.value,
            item.id
          ]
        );
      },
      handleSelectAll () {
        if (this.areAllSelected) {
          return this.$emit('input', []);
        }
        return this.$emit('input', this.filteredSelectAllChoices.map(c => c.id));
      }
    }
  };
</script>

<style>
  .all-items-block-filtration__search {
    margin-top: 16px;
  }

  .all-items-block__choices {
    overflow-x: hidden;
    overflow-y: auto;

    width: 380px;

    margin-top: 12px;
  }

  .all-items-search__label {
    font-size: var(--main-font-size);
    font-weight: var(--medium-font-weight);

    color: var(--text-color);
    line-height: var(--main-line-height);

    white-space: nowrap;

    display: inline-block;

    padding: 0;

    margin-bottom: 15px;
  }

  .all-items-block-choices__item {
    padding: 6px 0;
  }

  .all-items-block__select-all {
    display: flex;
    align-items: center;
  }

  .all-items-block-select-all__help-icon {
    margin-left: 4px;
  }

  .all-items-block_filtration__group {
    display: flex;
    flex-shrink: 0;
  }

  .all-items-block_filtration__group-item {
    flex: 1 1;
    align-self: flex-end;
  }

  .all-items-block_filtration__group-item:not(:first-child) {
    margin-left: 12px;
  }

  .all-items-block .bound-and-disabled-checkbox-m-help {
    width: 365px;
    height: 16px;

    margin: 10px 0;
  }

  .all-items-block .bound-and-disabled-checkbox-m-help:nth-child(2) {
    margin: 5px 0 0;
  }

  .all-items-block .m-checkbox.all-items-block-choices__item ~ .bound-and-disabled-checkbox-m-help {
    margin-top: 5px;
  }

  .all-items-block .bound-and-disabled-checkbox-m-help .all-items-block-choices__item {
    width: 365px;

    padding: 0;

    margin: 0;
  }

</style>
